import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Search, Loader2, ArrowRight, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Best places to visit in Hyderabad",
  "Weekend getaway from Hyderabad under ₹10k",
  "Ramoji Film City family package",
  "Hyderabad to Srisailam tour",
  "Adventure activities near Hyderabad",
  "Heritage walk in Old City",
];

export default function AISearchBar() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  // Enquiry form state
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ full_name: "", email: "", phone: "" });
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [enquiryLoading, setEnquiryLoading] = useState(false);

  const runSearch = async (query) => {
    const text = (query || q).trim();
    if (!text) return;
    setLoading(true);
    setOpen(true);
    setShowEnquiry(false);
    setEnquirySubmitted(false);
    try {
      const { data } = await api.post("/ai/search", { query: text });
      setAnswer(data.answer || "");
      setResults(data.results || []);
    } catch {
      setAnswer("Sorry, I couldn't process that. Try browsing our categories below.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!enquiryForm.full_name || !enquiryForm.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setEnquiryLoading(true);
    try {
      await api.post("/enquiries", {
        full_name: enquiryForm.full_name,
        email: enquiryForm.email,
        phone: enquiryForm.phone,
        message: `[AI Travel Assistant Query]\n\nQuestion: ${q}\n\nAI Response: ${answer}`,
        source: "ai_assistant",
        travel_date: "",
        adults: 1,
        children: 0,
      });
      setEnquirySubmitted(true);
      toast.success("Your enquiry has been sent! We'll get back to you soon.");
    } catch {
      toast.error("Failed to send enquiry. Please try again.");
    } finally {
      setEnquiryLoading(false);
    }
  };

  return (
    <div className="relative bg-white border border-border rounded-2xl shadow-lg" data-testid="ai-search-bar">
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal font-semibold mb-2">
          <Sparkles className="w-3.5 h-3.5" />
          Ask AI Travel Assistant
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
          className="flex gap-2"
        >
          <div className="flex-1 flex items-center gap-2 bg-[#F5FCFF] rounded-xl border border-border px-3 py-2">
            <Search className="w-4 h-4 text-teal shrink-0" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='e.g. "Best Hyderabad sightseeing package for family"'
              className="border-0 focus-visible:ring-0 px-0 h-8 bg-transparent"
              data-testid="ai-search-input"
            />
          </div>
          <Button type="submit" className="bg-saffron hover:bg-saffron-dark text-white h-11 px-5" disabled={loading} data-testid="ai-search-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask AI"}
          </Button>
        </form>

        {!open && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setQ(s);
                  runSearch(s);
                }}
                type="button"
                className="text-xs whitespace-nowrap px-3 py-1.5 rounded-full border border-border bg-secondary/40 hover:border-teal hover:text-teal transition-colors"
                data-testid={`ai-suggestion-${s.slice(0, 10).replace(/\s/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="border-t border-border p-4 sm:p-5 bg-secondary/20 rounded-b-2xl" data-testid="ai-search-results">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-teal" /> Thinking...
            </div>
          ) : (
            <>
              {answer && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-teal text-white flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <p className="text-sm text-foreground leading-relaxed" data-testid="ai-answer">{answer}</p>
                </div>
              )}
              {results.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  {results.slice(0, 4).map((p) => (
                    <Link
                      key={p.id}
                      to={`/${p.slug}`}
                      className="flex gap-3 bg-white border border-border rounded-xl p-2.5 hover:border-teal transition-colors"
                      data-testid={`ai-result-${p.slug}`}
                    >
                      <img src={p.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold line-clamp-1">{p.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{p.destination}</div>
                        <div className="text-sm font-bold text-teal mt-1">{formatINR(p.adult_price)}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-teal self-center shrink-0" />
                    </Link>
                  ))}
                </div>
              )}

              {/* Get Quote / Enquiry CTA */}
              {!showEnquiry && !enquirySubmitted && (
                <div className="bg-gradient-to-r from-teal/5 to-saffron/5 border border-dashed border-teal/30 rounded-xl p-3 mt-3">
                  <p className="text-sm text-muted-foreground mb-2">Need a customized quote? Share your details and our team will get back to you.</p>
                  <Button
                    onClick={() => setShowEnquiry(true)}
                    variant="outline"
                    className="border-teal text-teal hover:bg-teal hover:text-white"
                    data-testid="ai-get-quote-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Get a Free Quote
                  </Button>
                </div>
              )}

              {showEnquiry && !enquirySubmitted && (
                <form onSubmit={submitEnquiry} className="bg-white border border-border rounded-xl p-4 mt-3 space-y-3" data-testid="ai-enquiry-form">
                  <p className="text-sm font-semibold">Get a personalized quote</p>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Input
                      placeholder="Your Name *"
                      value={enquiryForm.full_name}
                      onChange={(e) => setEnquiryForm({ ...enquiryForm, full_name: e.target.value })}
                      required
                      data-testid="ai-enquiry-name"
                    />
                    <Input
                      placeholder="Phone *"
                      value={enquiryForm.phone}
                      onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                      required
                      data-testid="ai-enquiry-phone"
                    />
                    <Input
                      placeholder="Email (optional)"
                      type="email"
                      value={enquiryForm.email}
                      onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                      data-testid="ai-enquiry-email"
                    />
                  </div>
                  <Button type="submit" className="bg-saffron hover:bg-saffron-dark text-white" disabled={enquiryLoading} data-testid="ai-enquiry-submit">
                    {enquiryLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    Submit Enquiry
                  </Button>
                </form>
              )}

              {enquirySubmitted && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-3 flex items-center gap-3" data-testid="ai-enquiry-success">
                  <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                  <div className="text-sm text-green-800">Your enquiry has been submitted! Our team will contact you shortly.</div>
                </div>
              )}

              <button
                onClick={() => { setOpen(false); setAnswer(""); setResults([]); setQ(""); setShowEnquiry(false); setEnquirySubmitted(false); }}
                type="button"
                className="text-xs text-muted-foreground hover:text-teal mt-3"
                data-testid="ai-search-reset"
              >
                Clear results & ask something else
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
